import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, FadeIn } from "../../components/motion/Reveal";
import { getArticle, ARTICLES } from "../../data/articles";
import NotFound from "./NotFound";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <NotFound />;

  const others = ARTICLES.filter((a) => a.slug !== slug);

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid={`article-detail-${slug}`}>
      <SiteHeader />
      <section className="relative bg-nx-navy overflow-hidden pt-20 sm:pt-24 lg:pt-36 pb-14">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_60%,#060d1a_100%)]" />
        <div className="relative nx-wrap max-w-3xl">
          <FadeIn delay={0.15}>
            <Link to="/insights" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors mb-8" data-testid="article-back-link">
              <ArrowLeft size={14} /> All insights
            </Link>
          </FadeIn>
          <FadeIn delay={0.25}>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-[10px] tracking-[0.22em] uppercase text-nx-goldlight border border-nx-gold/40 px-3 py-1.5">{article.category}</span>
              <span className="text-[11px] tracking-[0.18em] uppercase text-slate-500">{fmtDate(article.date)} · {article.readTime}</span>
            </div>
          </FadeIn>
          <h1 className="font-marcellus text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.12]" data-testid="article-title">{article.title}</h1>
          <FadeIn delay={0.4}>
            <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed" data-testid="article-excerpt">{article.excerpt}</p>
          </FadeIn>
        </div>
      </section>

      <FadeIn delay={0.2}>
        <div className="nx-wrap max-w-3xl">
          <div className="nx-clip overflow-hidden border border-white/10">
            <img src={article.image} alt={article.title} className="w-full h-56 sm:h-80 object-cover" data-testid="article-image" />
          </div>
        </div>
      </FadeIn>

      <section className="py-14 sm:py-20">
        <div className="nx-wrap max-w-3xl">
          {article.body.map((s, i) => (
            <Reveal key={i} className="mb-12">
              <h2 className="font-marcellus text-2xl sm:text-3xl text-white mb-5" data-testid={`article-section-${i}`}>{s.h}</h2>
              {s.paras.map((p, j) => (
                <p key={j} className="text-base text-slate-300 leading-relaxed mb-4">{p}</p>
              ))}
            </Reveal>
          ))}
          <Reveal>
            <div className="border-t border-white/10 pt-10 mt-6">
              <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-5">More insights</p>
              <div className="flex flex-col gap-3">
                {others.map((o) => (
                  <Link key={o.slug} to={`/insights/${o.slug}`} className="group flex items-center justify-between border border-white/10 hover:border-nx-gold/50 px-5 py-4 transition-colors" data-testid={`article-more-${o.slug}`}>
                    <span className="font-marcellus text-white text-base sm:text-lg">{o.title}</span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-nx-goldlight shrink-0 ml-4">{o.category}</span>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
